import { usePlayer } from '../context/PlayerContext';
import { toFullUrl } from '../config/api';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, ChevronUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import QueuePanel from './QueuePanel';

// Measured height of the player bar (progress + content area + borders).
// Slightly padded so the last row of content never sits flush against the bar.
const PLAYER_HEIGHT_PX = 84;
// Ignore tiny scroll jitters so the bar doesn't flicker.
const SCROLL_THRESHOLD = 12;

export default function AudioPlayer() {
  const {
    currentTrack, isPlaying, currentTime, duration, volume,
    setVolume, play, pause, seek, skipNext, skipPrev, formatTime
  } = usePlayer();
  const rootRef = useRef(null);
  const [hidden, setHidden] = useState(false);

  // Reserve space at the bottom of every page while the player is mounted,
  // so no content or buttons end up trapped underneath the fixed bar.
  useEffect(() => {
    if (!currentTrack) {
      document.body.style.paddingBottom = '';
      document.documentElement.style.setProperty('--player-height', '0px');
      return;
    }
    // Use actual rendered height when possible, fall back to constant.
    const measure = () => {
      const h = rootRef.current?.offsetHeight || PLAYER_HEIGHT_PX;
      const pad = `${h + 8}px`;
      document.body.style.paddingBottom = pad;
      document.documentElement.style.setProperty('--player-height', pad);
    };
    measure();
    // Re-measure on resize / orientation change (mobile Safari needs this).
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      document.body.style.paddingBottom = '';
      document.documentElement.style.setProperty('--player-height', '0px');
    };
  }, [currentTrack]);

  // Auto-hide on scroll-down / reveal on scroll-up (Apple Music / YT Music style).
  useEffect(() => {
    if (!currentTrack) return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        // Always visible near the top & near the very bottom (user likely reaching for a button).
        if (y < 80 || y > maxY - 40) {
          setHidden(false);
        } else if (delta > SCROLL_THRESHOLD) {
          setHidden(true);
          lastY = y;
        } else if (delta < -SCROLL_THRESHOLD) {
          setHidden(false);
          lastY = y;
        }
        if (Math.abs(delta) > SCROLL_THRESHOLD) lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [currentTrack]);

  // When a track changes / starts playing, briefly ensure the player is visible.
  useEffect(() => { if (isPlaying) setHidden(false); }, [isPlaying, currentTrack?.id]);

  if (!currentTrack) return null;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      {/* Tiny reveal handle — always tappable when player is hidden */}
      <button
        type="button"
        onClick={() => setHidden(false)}
        aria-label="Show player"
        data-testid="player-reveal-handle"
        className="fixed bottom-0 left-1/2 -translate-x-1/2 z-[51] px-3 py-1 rounded-t-md transition-all duration-300"
        style={{
          background: 'rgba(10,10,10,0.8)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          borderLeft: '1px solid rgba(255,255,255,0.1)',
          borderRight: '1px solid rgba(255,255,255,0.1)',
          opacity: hidden ? 1 : 0,
          pointerEvents: hidden ? 'auto' : 'none',
          transform: `translate(-50%, ${hidden ? '0' : '10px'})`,
        }}
      >
        <ChevronUp className="w-3.5 h-3.5 text-white/70" />
      </button>

      <div
      ref={rootRef}
      data-testid="audio-player"
      data-hidden={hidden ? 'true' : 'false'}
      className="fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out"
      style={{
        background: 'rgba(10, 10, 10, 0.75)',
        backdropFilter: 'blur(24px) saturate(1.6)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.5)',
        transform: hidden ? 'translateY(100%)' : 'translateY(0)',
      }}
    >
      {/* Progress bar — thin line at top of player */}
      <div className="h-[3px] w-full relative group cursor-pointer"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const pct = (e.clientX - rect.left) / rect.width;
          seek(pct * (duration || 1));
        }}
        data-testid="player-progress-top"
      >
        <div className="absolute inset-0 bg-white/[0.06]" />
        <div
          className="absolute inset-y-0 left-0 transition-all duration-150"
          style={{
            width: `${progress}%`,
            background: `var(--primary)`,
            boxShadow: `0 0 8px rgba(var(--primary-rgb), 0.5)`,
          }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            left: `${progress}%`,
            transform: `translateX(-50%) translateY(-50%)`,
            background: 'var(--primary)',
            boxShadow: `0 0 10px rgba(var(--primary-rgb), 0.6)`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-3 items-center gap-4 px-4 py-2.5">
        {/* Track Info — Left */}
        <div className="flex items-center gap-3 min-w-0">
          {currentTrack.cover_image ? (
            <img
              src={toFullUrl(currentTrack.cover_image)}
              alt={currentTrack.title}
              className="w-11 h-11 object-cover flex-shrink-0 shadow-lg"
              style={{ borderRadius: 'var(--radius, 0px)' }}
            />
          ) : (
            <div className="w-11 h-11 bg-white/5 flex items-center justify-center flex-shrink-0"
              style={{ borderRadius: 'var(--radius, 0px)' }}>
              <Play className="w-4 h-4 text-white/30" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate leading-tight">{currentTrack.title}</p>
            <p className="text-xs text-white/40 truncate">{currentTrack.artist}</p>
          </div>
        </div>

        {/* Controls — Center */}
        <div className="flex flex-col items-center gap-1">
          <div className="flex items-center gap-3">
            <button
              data-testid="player-skip-prev"
              onClick={skipPrev}
              className="text-white/40 hover:text-white transition-colors p-1"
            >
              <SkipBack className="w-4 h-4" />
            </button>
            <button
              data-testid="player-play-pause"
              onClick={() => isPlaying ? pause() : play()}
              className="w-10 h-10 flex items-center justify-center text-white transition-all duration-200 hover:scale-105"
              style={{
                background: 'var(--primary)',
                borderRadius: 'var(--radius, 0px)',
                boxShadow: `0 4px 16px rgba(var(--primary-rgb), 0.35)`,
              }}
            >
              {isPlaying ? <Pause className="w-4.5 h-4.5" /> : <Play className="w-4.5 h-4.5 ml-0.5" />}
            </button>
            <button
              data-testid="player-skip-next"
              onClick={skipNext}
              className="text-white/40 hover:text-white transition-colors p-1"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 w-full max-w-xs">
            <span className="text-[10px] font-mono text-white/30 w-8 text-right">{formatTime(currentTime)}</span>
            <span className="text-[10px] font-mono text-white/30 w-8">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume — Right */}
        <div className="flex items-center justify-end gap-2">
          <QueuePanel />
          <button
            onClick={() => setVolume(volume > 0 ? 0 : 0.8)}
            className="text-white/40 hover:text-white transition-colors p-1"
            data-testid="player-mute"
          >
            {volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <div className="relative w-20 h-1 bg-white/10 group cursor-pointer"
            style={{ borderRadius: '2px' }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const v = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
              setVolume(v);
            }}
          >
            <div className="absolute inset-y-0 left-0 bg-white/50"
              style={{ width: `${volume * 100}%`, borderRadius: '2px' }} />
            <input
              type="range" min={0} max={1} step={0.01} value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="absolute inset-0 w-full opacity-0 cursor-pointer"
              data-testid="player-volume"
            />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
